import React, { useState, useEffect } from 'react';
import ReactMarkdown from 'react-markdown';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

function Trending({ clientId }) {
  const [analysis, setAnalysis] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (clientId) {
      fetchTrends();
    }
  }, [clientId]);

  const fetchTrends = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/clients/${clientId}/trends`);
      setAnalysis(response.data.analysis);
    } catch (error) {
      console.error('Error fetching trends:', error);
      setError('Failed to load trend analysis. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      className="rounded-2xl p-6"
      style={{
        backgroundColor: 'var(--bg-secondary)',
        borderWidth: '1px',
        borderStyle: 'solid',
        borderColor: 'var(--border-color)',
        boxShadow: 'var(--shadow-md)'
      }}
    >
      <div className="flex justify-between items-center mb-6">
        <div>
          <h3 className="text-2xl font-semibold" style={{ color: 'var(--text-primary)' }}>
            Longterm Trends
          </h3>
          <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary)' }}>
            AI-powered analysis of patterns and themes
          </p>
        </div>
        <button
          onClick={fetchTrends}
          disabled={loading}
          className="px-4 py-2 rounded-lg text-sm transition-all disabled:opacity-50"
          style={{
            backgroundColor: 'var(--accent-15)',
            color: 'var(--accent)',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'var(--accent)',
            opacity: 0.3
          }}
          onMouseEnter={(e) => {
            if (!loading) {
              e.currentTarget.style.backgroundColor = 'var(--accent-20)';
            }
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--accent-15)';
          }}
        >
          {loading ? 'Analyzing...' : 'Refresh Analysis'}
        </button>
      </div>

      {loading && !analysis && (
        <div className="text-center py-12">
          <div
            className="animate-spin rounded-full h-12 w-12 border-4 mx-auto mb-4"
            style={{
              borderColor: 'var(--accent-20)',
              borderTopColor: 'var(--accent)'
            }}
          ></div>
          <p style={{ color: 'var(--text-tertiary)' }}>Analyzing your session history...</p>
        </div>
      )}

      {error && (
        <div
          className="rounded-xl p-4"
          style={{
            backgroundColor: 'rgba(239, 68, 68, 0.1)',
            borderWidth: '1px',
            borderStyle: 'solid',
            borderColor: 'rgba(239, 68, 68, 0.3)',
            color: '#ef4444'
          }}
        >
          {error}
        </div>
      )}

      {analysis && !loading && (
        <div
          className="text-base p-6 rounded-lg leading-relaxed prose max-w-none"
          style={{
            backgroundColor: 'var(--bg-tertiary)',
            color: 'var(--text-primary)'
          }}
        >
          <ReactMarkdown
            components={{
              h1: ({node, ...props}) => <h1 className="font-bold mb-3 mt-4 text-2xl" style={{ color: 'var(--text-primary)' }} {...props} />,
              h2: ({node, ...props}) => <h2 className="font-bold mb-3 mt-6 text-xl" style={{ color: 'var(--text-primary)' }} {...props} />,
              h3: ({node, ...props}) => <h3 className="font-semibold mb-2 mt-4 text-lg" style={{ color: 'var(--accent)' }} {...props} />,
              h4: ({node, ...props}) => <h4 className="font-medium mb-2 mt-3 text-base" style={{ color: 'var(--text-secondary)' }} {...props} />,
              p: ({node, ...props}) => <p className="mb-3 leading-relaxed" style={{ color: 'var(--text-primary)' }} {...props} />,
              strong: ({node, ...props}) => <strong className="font-semibold" style={{ color: 'var(--text-primary)' }} {...props} />,
              em: ({node, ...props}) => <em className="italic" style={{ color: 'var(--text-secondary)' }} {...props} />,
              ul: ({node, ...props}) => <ul className="list-disc mb-3 ml-4 space-y-2" style={{ color: 'var(--text-primary)' }} {...props} />,
              ol: ({node, ...props}) => <ol className="list-decimal mb-3 ml-4 space-y-2" style={{ color: 'var(--text-primary)' }} {...props} />,
              li: ({node, ...props}) => <li className="leading-relaxed pl-1" style={{ color: 'var(--text-primary)' }} {...props} />,
              code: ({node, ...props}) => <code className="px-1.5 py-0.5 rounded text-sm" style={{ backgroundColor: 'var(--bg-secondary)', color: 'var(--accent)' }} {...props} />,
              blockquote: ({node, ...props}) => <blockquote className="border-l-2 pl-4 italic my-3" style={{ borderColor: 'var(--accent)', opacity: 0.5, color: 'var(--text-secondary)' }} {...props} />,
            }}
          >
            {analysis}
          </ReactMarkdown>
        </div>
      )}
    </div>
  );
}

export default Trending;
